import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { ItemCard, ItemCardSkeleton } from './ItemCard'
import type { Category } from '@/types'
import type { Language } from '@/types'

interface ItemGridProps {
  categories: Category[]
  language: Language
  isRTL: boolean
  hasPhotos: boolean
  hasCart: boolean
  onAddItem?: (item: any) => void
  onToggleFavorite?: (itemId: string) => void
  isFavorite?: (itemId: string) => boolean
  onViewPhoto?: (item: any) => void
  loading?: boolean
  skeletonCount?: number
}

export function ItemGrid({
  categories,
  language,
  isRTL,
  hasPhotos,
  hasCart,
  onAddItem,
  onToggleFavorite,
  isFavorite,
  onViewPhoto,
  loading = false,
  skeletonCount = 6,
}: ItemGridProps) {
  // Track collapsed state per category
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const toggleCollapse = (categoryId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <ItemCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-16 animate-fade-in">
        <div className="text-5xl mb-4">🔍</div>
        <h3 className="font-heading text-xl font-semibold text-primary mb-2">
          {language === 'fr' && 'Aucun résultat'}
          {language === 'ar' && 'لا توجد نتائج'}
          {language === 'en' && 'No results'}
        </h3>
        <p className="text-muted-foreground">
          {language === 'fr' && "Essayez d'autres termes de recherche"}
          {language === 'ar' && 'جرب كلمات بحث أخرى'}
          {language === 'en' && 'Try different search terms'}
        </p>
      </div>
    )
  }

  // Global running index so the stagger reads across the whole page
  let itemIndex = 0

  return (
    <div className="space-y-8">
      {categories.map((category, catIdx) => {
        const totalItems = category.subcategories.reduce(
          (sum, sub) => sum + sub.items.filter((i) => i.available).length,
          0
        )
        if (totalItems === 0) return null

        const isCollapsed = collapsed.has(category.id)
        const categoryName = getCatName(category, language)

        return (
          <section
            key={category.id}
            id={`category-section-${category.id}`}
            className="scroll-mt-44"
          >
            {/* ── Collapsible Category Header ── */}
            <button
              onClick={() => toggleCollapse(category.id)}
              className={`group w-full flex items-center justify-between gap-3 mb-4
                px-1 sm:px-2 py-2 rounded-button animate-fade-up
                ${isRTL ? 'flex-row-reverse' : ''}`}
              aria-expanded={!isCollapsed}
              style={{ animationDelay: `${catIdx * 60}ms` }}
            >
              <div className={`flex items-center gap-2.5 min-w-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary leading-none truncate">
                  {categoryName}
                </h2>
                <span className="shrink-0 text-xs font-semibold text-primary/70 bg-secondary/60 px-2 py-1 rounded-full">
                  {totalItems}
                </span>
              </div>
              <span className={`shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-secondary/50 text-primary transition-transform duration-300 ${isRTL ? 'rotate-90' : ''}`}>
                <ChevronDown
                  size={20}
                  strokeWidth={2.5}
                  className={`transition-transform duration-300 ${isCollapsed ? (isRTL ? 'rotate-90' : '-rotate-90') : ''}`}
                />
              </span>
            </button>

            {/* ── Collapsible Content ── */}
            <div className={`overflow-hidden transition-all duration-500 ease-out-expo ${isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[9999px] opacity-100'}`}>
              {category.subcategories.map((subcategory) => {
                const availableItems = subcategory.items.filter((item) => item.available)
                if (availableItems.length === 0) return null

                const subWithItems = category.subcategories.filter(
                  (s) => s.items.some((i) => i.available)
                )
                const showSubHeader = subWithItems.length > 1

                return (
                  <div key={subcategory.id} className="mb-6 last:mb-0">
                    {showSubHeader && (
                      <h3 className={`font-heading text-lg font-semibold text-primary/60 mb-3 px-1 ${isRTL ? 'text-right' : ''}`}>
                        {getSubName(subcategory, language)}
                      </h3>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {availableItems.map((item) => {
                        const idx = itemIndex++
                        return (
                          <ItemCard
                            key={item.id}
                            item={item}
                            language={language}
                            isRTL={isRTL}
                            hasPhotos={hasPhotos}
                            hasCart={hasCart}
                            index={idx}
                            onAdd={
                              onAddItem && item.available
                                ? () => onAddItem(item)
                                : undefined
                            }
                            onToggleFavorite={
                              onToggleFavorite
                                ? () => onToggleFavorite(item.id)
                                : undefined
                            }
                            isFavorite={isFavorite ? isFavorite(item.id) : false}
                            onViewPhoto={
                              onViewPhoto && hasPhotos && item.photo
                                ? () => onViewPhoto(item)
                                : undefined
                            }
                          />
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}

// ── helpers ────────────────────────────────────────────────────────────────
function getCatName(c: Category, language: Language) {
  if (language === 'fr') return c.name_fr
  if (language === 'ar') return c.name_ar
  return c.name_en
}

function getSubName(s: Category['subcategories'][number], language: Language) {
  if (language === 'fr') return s.name_fr
  if (language === 'ar') return s.name_ar
  return s.name_en
}
